import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ShieldCheck,
  Calendar,
  Hash,
  Play,
  Loader2,
  CheckCircle,
  AlertCircle,
  Code,
  ChevronDown,
  ChevronUp,
  Users,
  Copy,
  CreditCard,
  FileText,
  ClipboardList,
  Link2,
  Link2Off,
  Clock,
  Download,
} from 'lucide-react';
import { rewardIntegrityApi } from '../../services/api';

const todayIso = () => new Date().toISOString().split('T')[0];
const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const RewardIntegrityChecks = () => {
  const [activeTab, setActiveTab] = useState('duplicate'); // 'duplicate' or 'reconciliation'
  const [showQueryDup, setShowQueryDup] = useState(false);
  const [showQueryRecon, setShowQueryRecon] = useState(false);

  // Duplicate Reward Check state — baseline date pre-filled per the original check's default
  const [lookbackDays, setLookbackDays] = useState(1);
  const [dedupBaselineStart, setDedupBaselineStart] = useState('2026-08-15');

  // Task/Wallet Reconciliation state — defaults to yesterday, same as the original fixed query
  const [checkDate, setCheckDate] = useState(yesterdayIso());

  // Snapshot of the inputs actually submitted for the currently-displayed result,
  // so a filename can't drift from the data if the form inputs change afterward
  // without re-running the check.
  const [submittedBaselineStart, setSubmittedBaselineStart] = useState(dedupBaselineStart);

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      // api.js's response interceptor already unwraps to response.data for
      // every call except the one that sets _bypassUnwrap — this endpoint
      // returns { success, records, summary, query, executedAt } directly,
      // so `response` here already IS that object, not an axios envelope.
      return rewardIntegrityApi.runDuplicateRewardCheck(lookbackDays, dedupBaselineStart);
    },
  });

  const reconciliationMutation = useMutation({
    mutationFn: async () => {
      // Same unwrapping note as duplicateMutation above.
      return rewardIntegrityApi.runTaskWalletReconciliation(checkDate);
    },
  });

  const handleRunDuplicateCheck = () => {
    if (!lookbackDays || lookbackDays < 1) {
      alert('Please enter a lookback days value of 1 or more');
      return;
    }
    if (!dedupBaselineStart) {
      alert('Please select a dedup baseline start date');
      return;
    }
    setSubmittedBaselineStart(dedupBaselineStart);
    duplicateMutation.mutate();
  };

  const handleRunReconciliation = () => {
    if (!checkDate) {
      alert('Please select a check date');
      return;
    }
    reconciliationMutation.mutate();
  };

  const dupData = duplicateMutation.data;
  const dupRecords = dupData?.records || [];
  const dupSummary = dupData?.summary || {};

  const reconData = reconciliationMutation.data;
  const reconSummary = reconData?.summary || {};

  const getExportFileName = (extension) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `duplicate_reward_check_${submittedBaselineStart}_${timestamp}.${extension}`;
  };

  const exportHeaders = [
    'Consumer Code', 'Reward', 'Recurrence', 'Amount', 'Combo Rank', 'Combo Size',
    'Card Status', 'Txn Date (PST)', 'Transaction Code',
  ];
  const exportRows = () => dupRecords.map((r) => [
    r.consumer_code, r.reward_description, r.task_kind,
    parseFloat(r.transaction_amount || 0).toFixed(2), r.combo_rank, r.combo_size,
    r.card_issue_status, String(r.transaction_date_pst || '').replace('T', ' ').substring(0, 19),
    r.transaction_code,
  ]);

  const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

  const handleExportCSV = () => {
    if (!dupRecords.length) return;
    const rows = exportRows();
    const csvContent = [
      exportHeaders.join(','),
      ...rows.map((row) => row.map(csvEscape).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = getExportFileName('csv');
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getReconExportFileName = (extension) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `task_wallet_reconciliation_${reconSummary.check_date || checkDate}_${timestamp}.${extension}`;
  };

  const reconExportHeaders = [
    'Check Date', 'Tasks Completed', 'Wallet Txns Created', 'Matched',
    'Missing Txn Code', 'Wallet Txn Not Found', 'Delayed Credit',
  ];
  const reconExportRows = () => [[
    reconSummary.check_date,
    reconSummary.total_tasks_completed || 0,
    reconSummary.total_wallet_txns_created || 0,
    reconSummary.tasks_matched || 0,
    reconSummary.missing_wallet_txn_code || 0,
    reconSummary.wallet_txn_not_found || 0,
    reconSummary.delayed_credit || 0,
  ]];

  const handleExportReconCSV = () => {
    if (!reconData) return;
    const rows = reconExportRows();
    const csvContent = [
      reconExportHeaders.join(','),
      ...rows.map((row) => row.map(csvEscape).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = getReconExportFileName('csv');
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-yellow-500" />
            Reward Integrity Checks
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Catch duplicate reward payouts and reconcile task completions against wallet credits
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('duplicate')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'duplicate'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Copy className="w-4 h-4" /> Duplicate Reward Check
          </button>
          <button
            onClick={() => setActiveTab('reconciliation')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'reconciliation'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClipboardList className="w-4 h-4" /> Task/Wallet Reconciliation
          </button>
        </nav>
      </div>

      {/* ───────── Duplicate Reward Check tab ───────── */}
      {activeTab === 'duplicate' && (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Hash className="w-4 h-4 inline mr-1" /> Lookback Days
                </label>
                <input
                  type="number"
                  min="1"
                  value={lookbackDays}
                  onChange={(e) => setLookbackDays(e.target.value)}
                  placeholder="e.g. 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">How many days back to look for new duplicates</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" /> Dedup Baseline Start
                </label>
                <input
                  type="date"
                  value={dedupBaselineStart}
                  onChange={(e) => setDedupBaselineStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">When to start counting for the dedup baseline</p>
              </div>
              <div className="flex items-end md:col-span-2">
                <button
                  onClick={handleRunDuplicateCheck}
                  disabled={duplicateMutation.isPending}
                  className="flex items-center justify-center gap-2 px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {duplicateMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Running...</>
                  ) : (
                    <><Play className="w-4 h-4" /> Run Check</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {duplicateMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Error running duplicate reward check</p>
                <p className="text-sm text-red-600">{duplicateMutation.error?.message}</p>
              </div>
            </div>
          )}

          {dupData?.query && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setShowQueryDup(!showQueryDup)}
                className="w-full px-4 py-3 bg-slate-50 border-b border-gray-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
              >
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Code className="w-4 h-4 text-slate-600" />
                  SQL Query
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Click to {showQueryDup ? 'hide' : 'show'}</span>
                  {showQueryDup ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </div>
              </button>
              {showQueryDup && (
                <div className="p-4 bg-slate-900 overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{dupData.query}</pre>
                </div>
              )}
            </div>
          )}

          {dupData && (
            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg"><FileText className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{dupSummary.totalRecords?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">Duplicate Rows</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg"><Users className="w-5 h-5 text-amber-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{dupSummary.uniqueConsumers?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">Unique Consumers</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 rounded-lg"><CreditCard className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{dupSummary.withCard?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">With Card</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg"><CreditCard className="w-5 h-5 text-gray-500" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-600">{dupSummary.withoutCard?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">Without Card</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {dupData && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">
                  Duplicate Reward Rows ({dupRecords.length.toLocaleString()} total)
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Executed at {new Date(dupData.executedAt).toLocaleString()}
                  </div>
                  {dupRecords.length > 0 && (
                    <button
                      onClick={handleExportCSV}
                      className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                    >
                      <Download className="w-4 h-4" />
                      Export CSV
                    </button>
                  )}
                </div>
              </div>
              {dupRecords.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                  <p className="text-gray-500">No duplicate rewards found for the selected window</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consumer Code</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reward</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurrence</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Combo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Card Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Txn Date (PST)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {dupRecords.map((r, idx) => (
                        <tr key={`${r.transaction_id}-${idx}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{r.consumer_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{r.reward_description}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                              {r.task_kind}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                            ${parseFloat(r.transaction_amount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{r.combo_rank} of {r.combo_size}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              r.card_status_group === 'WITH_CARD' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {r.card_issue_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{r.transaction_date_pst?.toString().replace('T', ' ').substring(0, 19)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ───────── Task/Wallet Reconciliation tab ───────── */}
      {activeTab === 'reconciliation' && (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" /> Check Date
                </label>
                <input
                  type="date"
                  value={checkDate}
                  max={todayIso()}
                  onChange={(e) => setCheckDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Defaults to yesterday, same as the original fixed check</p>
              </div>
              <div className="flex items-end md:col-span-3">
                <button
                  onClick={handleRunReconciliation}
                  disabled={reconciliationMutation.isPending}
                  className="flex items-center justify-center gap-2 px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reconciliationMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Running...</>
                  ) : (
                    <><Play className="w-4 h-4" /> Run Check</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {reconciliationMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Error running reconciliation check</p>
                <p className="text-sm text-red-600">{reconciliationMutation.error?.message}</p>
              </div>
            </div>
          )}

          {reconData?.query && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setShowQueryRecon(!showQueryRecon)}
                className="w-full px-4 py-3 bg-slate-50 border-b border-gray-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
              >
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Code className="w-4 h-4 text-slate-600" />
                  SQL Query
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Click to {showQueryRecon ? 'hide' : 'show'}</span>
                  {showQueryRecon ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </div>
              </button>
              {showQueryRecon && (
                <div className="p-4 bg-slate-900 overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{reconData.query}</pre>
                </div>
              )}
            </div>
          )}

          {reconData && (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg"><Clock className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Checked date: {reconSummary.check_date}</p>
                    <p className="text-xs text-gray-500">Executed at {new Date(reconData.executedAt).toLocaleString()}</p>
                  </div>
                </div>
                <button
                  onClick={handleExportReconCSV}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>

              <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg"><ClipboardList className="w-5 h-5 text-blue-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{Number(reconSummary.total_tasks_completed || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Tasks Completed</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg"><Hash className="w-5 h-5 text-blue-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{Number(reconSummary.total_wallet_txns_created || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Wallet Txns Created</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-50 rounded-lg"><Link2 className="w-5 h-5 text-green-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{Number(reconSummary.tasks_matched || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Matched</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-lg"><Link2Off className="w-5 h-5 text-red-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{Number(reconSummary.missing_wallet_txn_code || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Missing Txn Code</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-lg"><AlertCircle className="w-5 h-5 text-red-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{Number(reconSummary.wallet_txn_not_found || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Wallet Txn Not Found</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg"><Clock className="w-5 h-5 text-amber-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{Number(reconSummary.delayed_credit || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Delayed Credit</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RewardIntegrityChecks;
